<template>
  <ClientOnly>
    <Loader v-if="isLoading" class="w-10 h-10 mx-auto mt-12" />

    <!-- Empty -->
    <StoreEmpty
      v-else-if="isEmpty"
      class="h-full py-24"
      :title="$t('empty_cart_title')"
      icon="NotoShoppingBags"
      :action-label="$t('start_shopping')"
      :action-route="{ name: 'biz', params: { biz: subdomain } }"
    />

    <div v-else class="cart-content dark:bg-gray-900 dark:text-gray-300">
      <!-- Container -->
      <div
        class="flex-grow w-full py-6 sm:px-8 lg:px-0 lg:max-w-4xl lg:mx-auto lg:flex"
      >
        <!-- Left -->
        <div class="space-y-6 lg:w-2/3 lg:pe-8">
          <slot
            :total="total"
            :minimum-amount="minimumOrderAmount"
            :errors="errors"
          />
        </div>

        <!-- Right -->
        <div class="mt-6 lg:mt-0 lg:w-1/3">
          <div class="cart-sticky">
            <!-- Loading -->
            <template v-if="isLoading">
              <ShimmerCartNote />
              <ShimmerCartSectionButton />
              <ShimmerCartSummary />
            </template>

            <template v-else>
              <!-- Note -->
              <StoreCartNote v-if="note" :note="note" />

              <!-- Coupon Code -->
              <StoreCartCoupon v-bind="{ appliedCoupon }" />

              <!-- Share Cart on Whatsapp -->
              <StoreCartWhatsappShareBtn v-if="!isEmpty" />

              <!-- Summary -->
              <StoreCartSummary
                v-bind="{
                  subTotal,
                  charges,
                  deliveryCharge,
                  total,
                  appliedCoupon,
                  errors,
                  checkoutAddonCharge,
                }"
              />
            </template>
          </div>
        </div>
      </div>
      <!-- Footer -->
      <div
        class="cart-footer dark:bg-gray-900 dark:text-gray-500 dark:border-t dark:border-gray-600 dark:shadow-2xl"
      >
        <div class="w-full lg:max-w-4xl lg:mx-auto lg:text-center">
          <div v-if="isLoading" class="p-4 lg:px-0">
            <ShimmerCartFooter />
          </div>

          <template v-else>
            <!-- Warning -->
            <StoreCartMinAmount
              v-if="!hasMinimumAmount"
              :amount="minimumOrderAmount"
              :note="minimumOrderNote"
            />

            <div class="flex items-center justify-between p-4 lg:px-0">
              <!-- Total -->
              <div class="text-start">
                <p class="text-xs leading-none text-gray-500">
                  {{ $t("total") }}
                </p>
                <Amount class="font-bold text-md" :value="total" />
              </div>

              <!-- Action -->
              <div class="ms-auto text-end">
                <!-- Minimum Cart Value Check -->
                <VyButton
                  v-if="!hasMinimumAmount"
                  theme="solid"
                  color="warning"
                  shape="rounded"
                  size="lg"
                  :label="$t('add_more_items')"
                  :to="{ name: 'biz' }"
                />

                <!-- Deliver Location Check -->
                <Query
                  v-else
                  :query="$options.shippingLocationsGQL"
                  :variables="{ hash }"
                >
                  <template #loading>
                    <div />
                  </template>
                  <template
                    #default="{
                      data: {
                        shippingLocations: { data },
                      },
                    }"
                  >
                    <VyButton
                      v-if="data?.length > 0 && !shippingLocationId"
                      size="lg"
                      color="warning"
                      shape="rounded"
                      theme="solid"
                      direction="rowReverse"
                      :label="$t('deliver_to')"
                      icon="MdiArrowRight"
                      @click="$nuxt.callHook('modal-open', 'shipping-location')"
                    />
                    <slot v-else name="action" :total="total" />
                  </template>
                </Query>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </ClientOnly>
</template>

<script>
import { useBizStore } from "@/store/index.js";
import { mapState } from "pinia";
import { useCartStore } from "~/store/cart";
import { useVepaarStore } from "@/store/store.js";
import { CHECKOUT_SETTINGS } from "@/graph/store/cart/gql.js";
import shippingLocationsGQL from "@/graph/store/shipping-locations.gql";

export default {
  shippingLocationsGQL,
  data() {
    return {
      minimumOrderNote: "",
      minimumOrderAmount: null,
      note: null,
    };
  },

  computed: {
    ...mapState(useBizStore, ["subdomain"]),
    ...mapState(useVepaarStore, ["hash"]),
    ...mapState(useCartStore, [
      "errors",
      "shippingLocationId",
      "isLoading",
      "isEmpty",
      "total",
      "subTotal",
      "deliveryCharge",
      "charges",
      "hasMinimumAmount",
      "appliedCoupon",
      "checkoutAddonCharge",
    ]),
  },

  created() {
    this.$apollo
      .query({
        query: CHECKOUT_SETTINGS,
        variables: {
          hash: this.hash,
        },
      })
      .then(({ data: { checkout } }) => {
        this.minimumOrderNote = checkout.minimumOrder.note;
        this.minimumOrderAmount = checkout.minimumOrder.amount;
        this.note = checkout.settings.note;
      })
      .catch((err) => console.error(err));
  },
};
</script>

<style>
.cart-content {
  @apply flex flex-col pb-[--headerHeight] lg:pb-0;
  min-height: calc(100vh - var(--headerHeight));
}

.cart-sticky {
  @apply space-y-6 lg:sticky;
  top: calc(var(--headerHeight) + theme("spacing.6"));
}

.cart-footer {
  @apply sticky z-10 bg-white lg:bottom-0 bottom-[--headerHeight];
  box-shadow: 0px 0px 20px 0 rgba(0, 0, 0, 0.1);
}
</style>
